import { IconButton, Tooltip } from '@ads/components-react';

import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChatBubbleBottomCenterTextIcon,
  MinusSmallIcon,
  PhotoIcon,
  Square2StackIcon,
  StopIcon,
  SwatchIcon,
  TagIcon,
  TrashIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/shared/ui/atoms';
import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import { useSlideVideoBackground } from '../../../hooks/useSlideVideoBackground';
import { ColorSwatch } from '../../atoms/ColorSwatch';
import { ElementToolbarItem } from '../../molecules/ElementToolbarItem';
import { useSlideEditorBackground } from './useSlideEditorBackground';

type BackgroundType = 'color' | 'image' | 'video';

function CircleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

interface BackgroundTypeButtonProps {
  readonly icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}

function BackgroundTypeButton({ icon: Icon, label, active, onClick }: BackgroundTypeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex shrink-0 items-center gap-quark px-micro py-quark ts-caption-xxs transition-colors ${
        active
          ? 'bg-ctx-layout-body text-ctx-content-title'
          : 'text-ctx-content-placeholder hover:text-ctx-content-base'
      }`}
    >
      <Icon className="h-micro w-micro shrink-0" />
      {label}
    </button>
  );
}

export function ElementToolbar() {
  const { t } = useTranslation('slideEditor');
  const ctx = useSlideEditorContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bgImageInputRef = useRef<HTMLInputElement>(null);
  const bgVideoInputRef = useRef<HTMLInputElement>(null);
  const {
    backgroundColor,
    backgroundImage,
    backgroundVideo,
    setBackground,
    setBackgroundImage,
    removeBackgroundImage,
  } = useSlideEditorBackground();
  const {
    upload: uploadVideo,
    remove: removeVideo,
    isUploading,
    isRemoving,
  } = useSlideVideoBackground();

  // Tipo de fundo efetivo da variante: vídeo tem precedência sobre imagem sobre cor.
  const activeType: BackgroundType = backgroundVideo
    ? 'video'
    : backgroundImage
      ? 'image'
      : 'color';
  // Aba selecionada (controlada). Sincroniza com o tipo efetivo ao trocar de variante.
  const [selectedType, setSelectedType] = useState<BackgroundType>(activeType);
  useEffect(() => {
    setSelectedType(activeType);
  }, [activeType]);

  const handleImageUpload = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        ctx.addElement('image', dataUrl);
      };
      reader.readAsDataURL(file);
      (e.target as HTMLInputElement).value = '';
    };
    fileInputRef.current.click();
  };

  const handleBackgroundImageUpload = () => {
    if (!bgImageInputRef.current) return;
    bgImageInputRef.current.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        setBackgroundImage(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
      (e.target as HTMLInputElement).value = '';
    };
    bgImageInputRef.current.click();
  };

  const handleBackgroundVideoUpload = () => {
    if (!bgVideoInputRef.current) return;
    bgVideoInputRef.current.onchange = e => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;
      void uploadVideo(file);
    };
    bgVideoInputRef.current.click();
  };

  // Troca de aba: aplica a exclusividade entre os tipos de fundo.
  const handleSelectType = (type: BackgroundType) => {
    setSelectedType(type);
    if (type !== 'video' && backgroundVideo) void removeVideo();
    if (type !== 'image' && backgroundImage) removeBackgroundImage();
  };

  return (
    <div className="at-scrollbar flex w-full flex-row items-center gap-quark overflow-x-auto px-nano py-quark [&::-webkit-scrollbar]:h-[0.75rem]">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" />
      <input ref={bgImageInputRef} type="file" accept="image/*" className="hidden" />
      <input ref={bgVideoInputRef} type="file" accept=".mp4,.webm,.mov" className="hidden" />

      {/* ── Conteúdo: texto, tag, imagem ─────────── */}
      <ElementToolbarItem
        icon={ChatBubbleBottomCenterTextIcon}
        label={t('toolbar.addText')}
        onClick={() => ctx.addElement('text')}
      />
      <ElementToolbarItem
        icon={TagIcon}
        label={t('toolbar.addTag')}
        onClick={() => ctx.addElement('tag')}
      />
      <ElementToolbarItem
        icon={PhotoIcon}
        label={t('toolbar.addImage')}
        onClick={handleImageUpload}
      />

      <div className="mx-quark h-xxxs w-[0.125rem] shrink-0 bg-ctx-layout-border" />

      {/* ── Formas ───────────────────────────────── */}
      <ElementToolbarItem
        icon={StopIcon}
        label={t('toolbar.addRect')}
        onClick={() => ctx.addElement('rect')}
      />
      <ElementToolbarItem
        icon={CircleIcon}
        label={t('toolbar.addCircle')}
        onClick={() => ctx.addElement('circle')}
      />
      <ElementToolbarItem
        icon={MinusSmallIcon}
        label={t('toolbar.addLine')}
        onClick={() => ctx.addElement('line')}
      />

      <div className="mx-quark h-xxxs w-[0.125rem] shrink-0 bg-ctx-layout-border" />

      {/* ── Histórico ────────────────────────────── */}
      <div className="flex shrink-0 items-center rounded-sm border-hairline border-ctx-layout-border">
        <Tooltip title={t('topBar.undo')} side="bottom">
          <IconButton
            icon={ArrowUturnLeftIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.undo')}
            onClick={ctx.undo}
            disabled={!ctx.canUndo}
          />
        </Tooltip>
        <div className="w-[0.125rem] self-stretch bg-ctx-layout-border" />
        <Tooltip title={t('topBar.redo')} side="bottom">
          <IconButton
            icon={ArrowUturnRightIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.redo')}
            onClick={ctx.redo}
            disabled={!ctx.canRedo}
          />
        </Tooltip>
      </div>

      {/* ── Ordem de camadas ─────────────────────── */}
      <div className="flex shrink-0 items-center rounded-sm border-hairline border-ctx-layout-border">
        <Tooltip title={t('topBar.bringForward')} side="bottom">
          <IconButton
            icon={ArrowUpIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.bringForward')}
            onClick={ctx.bringForward}
          />
        </Tooltip>
        <div className="w-[0.125rem] self-stretch bg-ctx-layout-border" />
        <Tooltip title={t('topBar.sendBackward')} side="bottom">
          <IconButton
            icon={ArrowDownIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.sendBackward')}
            onClick={ctx.sendBackward}
          />
        </Tooltip>
      </div>

      {/* ── Operações de objeto ───────────────────── */}
      <div className="flex shrink-0 items-center rounded-sm border-hairline border-ctx-layout-border">
        <Tooltip title={t('topBar.duplicate')} side="bottom">
          <IconButton
            icon={Square2StackIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.duplicate')}
            onClick={ctx.duplicateSelected}
          />
        </Tooltip>
        <div className="w-[0.125rem] self-stretch bg-ctx-layout-border" />
        <Tooltip title={t('topBar.delete')} side="bottom">
          <IconButton
            icon={TrashIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.delete')}
            onClick={ctx.deleteSelected}
          />
        </Tooltip>
      </div>

      <div className="mx-quark h-xxxs w-[0.125rem] shrink-0 bg-ctx-layout-border" />

      {/* ── Fundo do canvas ───────────────────────── */}
      <div className="flex shrink-0 items-center gap-nano rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-micro py-quark">
        {/* Seletor de tipo de fundo: Cor / Imagem / Vídeo (mutuamente exclusivos) */}
        <div className="flex shrink-0 items-center rounded-sm border-hairline border-ctx-layout-border">
          <BackgroundTypeButton
            icon={SwatchIcon}
            label={t('toolbar.backgroundTypeColor')}
            active={selectedType === 'color'}
            onClick={() => handleSelectType('color')}
          />
          <div className="w-[0.125rem] self-stretch bg-ctx-layout-border" />
          <BackgroundTypeButton
            icon={PhotoIcon}
            label={t('toolbar.backgroundTypeImage')}
            active={selectedType === 'image'}
            onClick={() => handleSelectType('image')}
          />
          <div className="w-[0.125rem] self-stretch bg-ctx-layout-border" />
          <BackgroundTypeButton
            icon={VideoCameraIcon}
            label={t('toolbar.backgroundTypeVideo')}
            active={selectedType === 'video'}
            onClick={() => handleSelectType('video')}
          />
        </div>

        <div className="mx-quark h-xxxs w-[0.125rem] shrink-0 bg-ctx-layout-border" />

        {/* Controles da cor (base para os tipos Cor e Imagem) */}
        {selectedType !== 'video' && (
          <div className="w-[7rem]">
            <ColorSwatch
              value={backgroundColor}
              onChange={setBackground}
              label={t('toolbar.backgroundColor')}
            />
          </div>
        )}

        {/* Controles da imagem de fundo */}
        {selectedType === 'image' &&
          (backgroundImage ? (
            <>
              <div
                className="rounded-quark h-xxs w-xxs shrink-0 border-hairline border-ctx-layout-border bg-cover bg-center"
                style={{ backgroundImage: `url(${backgroundImage})` }}
              />
              <Tooltip title={t('toolbar.backgroundImageChange')} side="bottom">
                <IconButton
                  icon={ArrowPathIcon}
                  ariaLabel={t('toolbar.backgroundImageChange')}
                  hierarchy="tertiary"
                  size="sm"
                  onClick={handleBackgroundImageUpload}
                />
              </Tooltip>
              <Tooltip title={t('toolbar.backgroundImageRemove')} side="bottom">
                <IconButton
                  icon={XMarkIcon}
                  ariaLabel={t('toolbar.backgroundImageRemove')}
                  hierarchy="tertiary"
                  size="sm"
                  onClick={removeBackgroundImage}
                />
              </Tooltip>
            </>
          ) : (
            <Tooltip title={t('toolbar.backgroundImageAdd')} side="bottom">
              <IconButton
                icon={PhotoIcon}
                ariaLabel={t('toolbar.backgroundImageAdd')}
                hierarchy="tertiary"
                size="sm"
                onClick={handleBackgroundImageUpload}
              />
            </Tooltip>
          ))}

        {/* Controles do vídeo de fundo */}
        {selectedType === 'video' &&
          (isUploading || isRemoving ? (
            <div className="flex shrink-0 items-center gap-quark px-quark">
              <Spinner size="sm" />
              <span className="text-ctx-content-placeholder ts-caption-xxs">
                {isUploading
                  ? t('toolbar.backgroundVideoUploading')
                  : t('toolbar.backgroundVideoRemoving')}
              </span>
            </div>
          ) : backgroundVideo ? (
            <>
              <video
                src={backgroundVideo}
                muted
                playsInline
                className="rounded-quark h-xxs w-xxs shrink-0 border-hairline border-ctx-layout-border object-cover"
              >
                <track kind="captions" />
              </video>
              <Tooltip title={t('toolbar.backgroundVideoChange')} side="bottom">
                <IconButton
                  icon={ArrowPathIcon}
                  ariaLabel={t('toolbar.backgroundVideoChange')}
                  hierarchy="tertiary"
                  size="sm"
                  onClick={handleBackgroundVideoUpload}
                />
              </Tooltip>
              <Tooltip title={t('toolbar.backgroundVideoRemove')} side="bottom">
                <IconButton
                  icon={XMarkIcon}
                  ariaLabel={t('toolbar.backgroundVideoRemove')}
                  hierarchy="tertiary"
                  size="sm"
                  onClick={() => void removeVideo()}
                />
              </Tooltip>
            </>
          ) : (
            <Tooltip title={t('toolbar.backgroundVideoAdd')} side="bottom">
              <IconButton
                icon={VideoCameraIcon}
                ariaLabel={t('toolbar.backgroundVideoAdd')}
                hierarchy="tertiary"
                size="sm"
                onClick={handleBackgroundVideoUpload}
              />
            </Tooltip>
          ))}
      </div>
    </div>
  );
}
